import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Platform analytics, read from Prona Scout's own database.
 *
 * A separate api slice from analyticsServices on purpose. That one is backed by
 * Google Analytics and is currently returning PERMISSION_DENIED for every call
 * — the service account has no access to the GA4 property. Keeping these apart
 * means a GA outage degrades the GA tabs only, and this tab keeps working.
 *
 * Backend: kosovascout_back, src/reports/ — mounted at /api/reports.
 * Platform staff only; the endpoints 401 otherwise.
 */
export const platformApi = createApi({
    reducerPath: 'platformApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST}/api/reports`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    keepUnusedDataFor: 300,
    tagTypes: ['Platform'],
    endpoints: (build) => ({
        getPlatformAnalytics: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return qs ? `/platform/analytics?${qs}` : '/platform/analytics';
            },
            providesTags: ['Platform'],
        }),
        getWebTraffic: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return qs ? `/platform/web-traffic?${qs}` : '/platform/web-traffic';
            },
            providesTags: ['Platform'],
        }),
        getMostVisited: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return qs ? `/platform/most-visited?${qs}` : '/platform/most-visited';
            },
            providesTags: ['Platform'],
        }),
        getDeletedUsers: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return qs ? `/platform/deleted-users?${qs}` : '/platform/deleted-users';
            },
            providesTags: ['Platform'],
        }),

        /* Every agent across every agency. The company-scoped leaderboard in
           companyAnalytics answers this for one agency only, and returns an
           empty list to a platform admin, who has no company. */
        getAgentReport: build.query({
            query: (params = {}) => {
                const qs = new URLSearchParams(params).toString();
                return qs ? `/platform/agents?${qs}` : '/platform/agents';
            },
            providesTags: ['Platform'],
        }),
    }),
});

export const {
    useGetPlatformAnalyticsQuery,
    useGetWebTrafficQuery,
    useGetMostVisitedQuery,
    useGetDeletedUsersQuery,
    useGetAgentReportQuery,
} = platformApi;
